import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, styled } from "@mui/material/styles";
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  Link,
  Paper
} from "@mui/material";
import * as yup from "yup";

// Project imports
import AuthFooter from "components/AuthFooter";
import ResetPasswordForm from "components/forms/ResetPasswordForm";
import PasswordRequirements from "components/forms/PasswordRequirements";
import buildRoute from "../components/buildRoute";
import { getCsrfToken } from "../utils/csrf";

const emailSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid password")
    .required("Email is required"),
});

const PageWrapper = styled("div")(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.primary.light,
  minHeight: "100vh",
}));

const ResetPassword = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
  const csrfToken = getCsrfToken();

  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  // hooks
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenError, setTokenError] = useState(false);
  const [password, setPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  // Validate the presence of the "token" parameter in the URL
  useEffect(() => {
    if (!token) {
      // todo; also link back to the request page
      setTokenError("Invalid link.");
      setIsValidatingToken(false);
      return;
    }

    // If token is present, proceed with validation
    const validateToken = async () => {
      try {
        const response = await fetch(`/api/forgot-password-token/${token}/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
        });

        if (!response.ok) {
          throw new Error("Invalid or expired token.");
        }

        setIsValidatingToken(false);
      } catch (error) {
        setTokenError(error.message);
        setIsValidatingToken(false);
      }
    };

    validateToken();
  }, []); // Empty dependency array ensures this runs only once on mount

  const handleFormSubmit = async (data) => {
    const password = data.password;

    setError(false);
    setSuccess(false);

    // Send update password request
    try {
      const response = await fetch(`/api/forgot-password-token/${token}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        setTimeout(() => {
          navigate("/login");
        }, 5 * 1000); // 5 seconds
        return setSuccess("Password updated successfully. You will now be redirected to the login page.");
      } else if (response.status === 400) {
        return setError("Password was not accepted. This may be due to the password being too similar to a previous password or not being strong enough. Choose a different password and try again.")
      } else {
        setTimeout(() => {
            navigate("/forgot-password");
        }, 10 * 1000); // 10 seconds
        return setError("Failed to update password. Please try again. If the issue persists, reach out to help@techallies.org for support.");
      } 
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <PageWrapper>
      <Grid
        container
        direction="column"
        justifyContent="flex-end"
        sx={{ minHeight: "100vh" }}
      >
        <Grid item xs={12}>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: "calc(100vh - 68px)" }}
          >
            <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
              <Paper
                square
                sx={{
                  maxWidth: { xs: 400, lg: 450 },
                  margin: { xs: 2.5, md: 3 },
                  "& > *": {
                    flexGrow: 1,
                    flexBasis: "50%",
                  },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.light",
                    px: 2,
                    py: 3,
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="h1"
                      color="inherit"
                      sx={{ fontWeight: 600 }}
                    >
                      PAD
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    color="inherit"
                    sx={{ fontWeight: 500 }}
                  >
                    From Community Tech Alliance
                  </Typography>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 3, lg: 4, xl: 5 } }}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Grid item xs={12}>
                      <Grid
                        container
                        direction={matchDownSM ? "column-reverse" : "row"}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid item>
                          <Stack
                            alignItems="center"
                            justifyContent="center"
                            spacing={1}
                          >
                            <Typography
                              variant="caption"
                              fontSize="16px"
                              color="inherit"
                              textAlign={matchDownSM ? "center" : "inherit"}
                              sx={{ fontWeight: "bold" }}
                            >
                              Create a new password
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid
                        container
                        direction="column"
                        justifyContent="center"
                        spacing={2}
                      >
                        <Grid item xs={12}>
                          {isValidatingToken && ("Loading...")}
                          {tokenError && (
                            <>
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ textAlign: "center" }}
                              >
                                {tokenError}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ textAlign: "center", marginTop: "12px" }}
                              >
                                <a href="/forgot-password">
                                  Request a new password reset
                                </a>
                              </Typography>
                            </>
                          )}
                          {!isValidatingToken && !tokenError && (
                            <>
                              <ResetPasswordForm
                                onSubmit={handleFormSubmit}
                                isLoading={isValidatingToken}
                                error={error}
                                success={success}
                                onPasswordChange={(newPassword) => setPassword(newPassword)}
                                isPasswordValid={isPasswordValid}
                              />
                              <PasswordRequirements password={password} onValidationChange={setIsPasswordValid} />
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  maxWidth: { xs: 400, lg: 450 },
                  margin: { xs: 2.5, md: 3 },
                }}
              >
                <Typography
                  variant="caption"
                  fontSize="12px"
                  color="inherit"
                  textAlign="center"
                >
                  Trouble logging in? Check our{" "}
                  <Link href="https://cta.statuspage.io/" target="_blank">
                    Status Page
                  </Link>
                  &nbsp;or click the &apos;Help&apos; button below
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ m: 3, mt: 1 }}>
          <AuthFooter />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

const routeArgs = {
  element: <ResetPassword />,
};

export default buildRoute(routeArgs);

